import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { SectionLabel, CTAGroup } from "@/components/bits";
import { IMAGES, SERVICES, PROCESS } from "@/lib/site";
import { Check } from "lucide-react";

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <div data-testid="page-services" className="pt-32">
      <header className="mx-auto max-w-[1600px] px-6 pb-20 md:px-12 md:pb-28">
        <Reveal>
          <SectionLabel>Capabilities</SectionLabel>
          <h1 className="mt-8 max-w-5xl font-serif text-5xl font-300 leading-[0.98] md:text-8xl">
            Every system, <span className="italic accent-text">considered</span>.
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
            Three signature disciplines lead our work, supported by the infrastructure that makes a smart home dependable — engineered, documented, and concealed.
          </p>
        </Reveal>
      </header>

      <div className="border-t border-white/10">
        {SERVICES.map((s, i) => (
          <section
            key={s.slug}
            id={s.slug}
            data-testid={`service-section-${s.slug}`}
            className={`scroll-mt-28 border-b border-white/10 ${i % 2 ? "bg-isav-bg2" : "bg-isav-bg"}`}
          >
            <div className={`mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-12 px-6 py-20 md:px-12 md:py-28 lg:grid-cols-2 lg:gap-20 ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <Reveal className="[direction:ltr]">
                <div className="hover-media relative aspect-[5/4] overflow-hidden border border-white/10">
                  <img src={IMAGES[s.image]} alt={s.title} className="h-full w-full object-cover" />
                  {!s.primary && (
                    <span className="absolute left-5 top-5 border border-white/20 bg-isav-bg/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 backdrop-blur">
                      Supporting capability
                    </span>
                  )}
                </div>
              </Reveal>
              <Reveal delay={0.1} className="[direction:ltr]">
                <span className="font-mono text-sm text-isav-green">{s.index}</span>
                <h2 className="mt-5 font-serif text-4xl font-400 md:text-5xl">{s.title}</h2>
                <p className="mt-3 text-lg italic text-white/50">{s.tagline}</p>
                <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/60 md:text-base">{s.summary}</p>
                <ul className="mt-8 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-white/70">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-isav-green" /> {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </section>
        ))}
      </div>

      <section className="bg-isav-bg py-28 md:py-36" data-testid="process">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Reveal>
            <SectionLabel>The process</SectionLabel>
            <h2 className="mt-8 font-serif text-4xl font-300 md:text-6xl">From plans to living systems.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08} className="bg-isav-bg p-10">
                <span className="font-mono text-sm text-isav-green">{p.step}</span>
                <h3 className="mt-6 font-serif text-2xl">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/55">{p.text}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-16"><CTAGroup /></div>
        </div>
      </section>
    </div>
  );
}
